/*
The notices of the XOL-596 workflow COMMANDS (UX spec items 5, 6, 10, 11, 12).

Pure builders: each takes what the command's adopt left on the rows and returns
the options of a section of the C-12 save-status card (`savePopup.notify`; the
same { type, title, message, duration } object `$notify` took), or, for a success,
the { type, message } whose `message` goes to `savePopup.success`. The copy is SPA
copy built from the adopted rows (UF1); `data.message` is only a fallback.

    kind     'prgApproval' (PRG steps 1 / 3), 'complete' / 'reopen'
             (a step), 'reject', 'completeRequest', and the approval lock:
             'lock' / 'unlock' (a step), 'completeLock' (Complete Step with
             {lock: true})
    ctx      { root, step, action, choice, rtx, step2p1, lastStep }
               root      the app root (formatDate / formatDateTime)
               step      the row the command acted on (adopted)
               action    the action row (adopted)
               choice    what the user asked for ('Approved', ...), for the
                         "choose it again" sentence
               step2p1   the Tower Co step 2 row (reject)
               lastStep  the last party-1 step (complete request)

ES5, no optional chaining or nullish coalescing.
------------------------------------------------------------------------ */

import { copyText } from '@/lib/requestSyncCopy'
import { closedLead, completedLeadFor, completedFooter } from '@/lib/syncNotices'
import { approvalStepKind, stepTitle } from '@/lib/approvalLock'
import { formatNoticeDateTime } from '@/lib/noticeDates'

function datetime (ctx, v) {
  return formatNoticeDateTime(ctx.root, v)
}

function status (row) {
  return row && row.approvedStatus ? row.approvedStatus : 'Pending'
}

// The word for a step's approval as the server holds it now: its status on steps 2
// and 5, ticked / not on step 3 and the PRG steps.
function approvalWord (step, ctx) {
  var kind = approvalStepKind(ctx.action, step)

  if (kind === 'step3' || kind === 'prg') {
    return step && step.approved ? 'approved' : 'Pending'
  }

  return status(step)
}

// "locked by {who} on {when}" parameters of an adopted step; `by` is null for a
// lock the one-time migration took (nobody is named, nothing is dated).
function lockParams (step, ctx) {
  var by = step && step.approvalLocked_by ? step.approvalLocked_by : null

  return {
    by: by,
    datetime: by && step.approvalLocked_at ? datetime(ctx, step.approvalLocked_at) : null,
    n: step ? step.stepNo : null,
    docType: ctx.action && ctx.action.state && ctx.action.state.step3 ? ctx.action.state.step3.docType : null
  }
}

/**
 * A 409 other than request_closed (UX item 5): `stale_decision`, `invalid_state`,
 * anything else. All `warning`, `duration: 0` (they ask the user to act), except the
 * two approval-LOCK ones (a lock / Lock & complete that found no decision to lock, or a
 * decision that changed after the prompt), which the UX spec's notices table gives
 * 5500 ms: nothing is waiting on the user, the current approval is simply shown.
 */
export function conflictNotice (kind, out, ctx) {
  var code = out && out.code
  var message = out && out.data ? (out.data.message || out.data.error) : null
  var step = ctx.step || {}
  var title
  var body
  var duration = 0

  if (code === 'stale_decision' && kind === 'prgApproval') {
    title = copyText('requestSync.cmd.prgApproval.stale.title')
    body = copyText('requestSync.cmd.prgApproval.stale.body')
  } else if (code === 'invalid_state' && (kind === 'complete' || kind === 'reopen')) {
    title = copyText('requestSync.cmd.step.invalid.title')
    body = copyText('requestSync.cmd.step.invalid.body', {
      completed: !!step.completed,
      n: step.stepNo,
      by: step.completed_by,
      datetime: step.completed_at ? datetime(ctx, step.completed_at) : null
    })
  } else if (kind === 'lock' && (code === 'invalid_state' || code === 'stale_decision')) {
    duration = 5500
    var lp = lockParams(step, ctx)
    title = copyText(code === 'stale_decision' ? 'requestSync.cmd.lock.stale.title' : 'requestSync.cmd.lock.invalid.title')
    body = code === 'stale_decision'
      ? copyText('requestSync.cmd.lock.stale.body', { by: step.approved_by, n: step.stepNo, status: approvalWord(step, ctx) })
      : copyText('requestSync.cmd.lock.invalid.body', { n: lp.n, status: approvalWord(step, ctx) })
  } else if (kind === 'completeLock' && (code === 'invalid_state' || code === 'stale_decision')) {
    duration = 5500
    title = copyText('requestSync.cmd.completeLock.invalid.title', { n: step.stepNo })
    body = code === 'stale_decision'
      ? copyText('requestSync.cmd.completeLock.stale.body', { by: step.approved_by, n: step.stepNo, status: approvalWord(step, ctx) })
      : copyText('requestSync.cmd.completeLock.invalid.body', { n: step.stepNo, status: approvalWord(step, ctx) })
  } else if (code === 'invalid_state' && kind === 'reject') {
    title = copyText('requestSync.cmd.reject.invalid.title')
    body = copyText('requestSync.cmd.reject.invalid.body', { status: status(ctx.step2p1) })
  } else if (code === 'invalid_state' && kind === 'completeRequest') {
    var last = ctx.lastStep || step
    title = copyText('requestSync.cmd.complete.invalid.title', { rtx: ctx.rtx })
    body = copyText('requestSync.cmd.complete.invalid.body', { n: last.stepNo, status: status(last) })
  } else {
    title = copyText('requestSync.cmd.fallback.title')
    body = copyText('requestSync.cmd.fallback.body', { message: message })
  }

  return { type: 'warning', title: title, message: body, duration: duration }
}

/**
 * 409 request_closed on a command (UX item 6). The drawer swaps to
 * CompletedRequest on the adopt; this explains why the change wasn't applied.
 */
export function closedNotice (ctx) {
  var lead = closedLead(ctx.root, { meta: { actionId: ctx.action ? ctx.action.id : null } })

  return { type: 'warning', title: lead.title, message: lead.body, duration: 0 }
}

/**
 * The command was not applied and the control went back (UX item 11): a 403, a
 * network failure whose re-GET lacks the outcome, a refusal.
 */
export function notSavedNotice (kind, ctx) {
  var step = ctx.step || {}
  var message

  switch (kind) {
    case 'complete': message = copyText('requestSync.notSaved.complete', { n: step.stepNo }); break
    case 'reopen': message = copyText('requestSync.notSaved.reopen', { n: step.stepNo }); break
    case 'reject': message = copyText('requestSync.notSaved.reject'); break
    case 'completeRequest': message = copyText('requestSync.notSaved.completeRequest', { rtx: ctx.rtx }); break
    case 'lock': message = copyText('requestSync.notSaved.lock'); break
    case 'unlock': message = copyText('requestSync.notSaved.unlock'); break
    case 'completeLock': message = copyText('requestSync.notSaved.completeLock', { n: step.stepNo }); break
    default: message = copyText('requestSync.notSaved.approval')
  }

  return { type: 'error', title: copyText('requestSync.notSaved.title'), message: message, duration: 5500 }
}

/**
 * The user stopped waiting for a command the server has not answered: the
 * outcome is unknown, the control is back to what the server last said, and a
 * late answer will still be adopted.
 */
export function unknownNotice () {
  return { type: 'warning', title: copyText('requestSync.cmd.unknown.title'), message: copyText('requestSync.cmd.unknown.body'), duration: 0 }
}

/** A 422 on a command: a bug signal (UX item 10). */
export function readOnlyNotice (out) {
  var err = out && out.err
  var h = err && err.config && err.config.headers
  var requestId = h && h['X-Request-Id']

  return {
    type: 'error',
    title: copyText('requestSync.readOnly.title'),
    message: copyText('requestSync.readOnly.body') + (requestId ? ' ' + copyText('requestSync.readOnly.reference', { requestId: requestId }) : ''),
    duration: 0
  }
}

/** The request is gone (a 404 and the re-GET is a 404 too). */
export function goneNotice () {
  return { type: 'error', title: copyText('requestSync.gone.title'), message: copyText('requestSync.gone.body'), duration: 0 }
}

/** The success message of an own reject / complete (UX item 12). */
export function doneMessage (kind, ctx) {
  var message = kind === 'reject'
    ? copyText('requestSync.done.reject', { rtx: ctx.rtx, id: ctx.action.id })
    : copyText('requestSync.done.complete', { rtx: ctx.rtx, id: ctx.action.id, status: ctx.action.status })

  return { type: 'success', message: message }
}

/**
 * A change was refused because the approval is LOCKED (403 approval_locked on an
 * approval command; the refusal's rows are already adopted, so the step carries
 * who locked it and when). `ctx.canUnlock` says whether this viewer may unlock it.
 */
export function lockedNotice (kind, ctx) {
  var step = ctx.step || {}
  var p = lockParams(step, ctx)
  var group = 'prgApproval'
  var title = copyText('requestSync.cmd.' + group + '.locked.title')
  var body = copyText('requestSync.cmd.' + group + '.locked.body', p) +
    copyText(ctx.canUnlock ? 'requestSync.approvalLock.suffix.canUnlock' : 'requestSync.approvalLock.suffix.askUnlock')

  return { type: 'warning', title: title, message: body, duration: 5500 }
}

/** The success text of a lock / unlock / Lock & complete (UX "Successes"). */
export function lockDoneMessage (kind, ctx) {
  var step = ctx.step || {}

  if (kind === 'unlock') {
    return { type: 'success', message: copyText('requestSync.approvalLock.done.unlocked', { n: step.stepNo }) }
  }

  if (kind === 'completeLock') {
    return { type: 'success', message: copyText('requestSync.approvalLock.done.completeLocked', { n: step.stepNo }) }
  }

  return {
    type: 'success',
    message: copyText('requestSync.approvalLock.done.locked', {
      steps: ctx.steps || [step.stepNo],
      prefix: ctx.prefix || ''
    })
  }
}

// Why one approval of a closing drawer was not locked (the summary's "reason").
function failedReason (item, ctx) {
  var out = item.out || {}
  var step = item.step
  var kind = approvalStepKind(ctx.action, step)

  if (out.status === 'conflict' && out.code === 'stale_decision') {
    return copyText('requestSync.approvalLock.failed.reason.changed', { by: step.approved_by, status: approvalWord(step, ctx) })
  }

  if (out.status === 'conflict' && out.code === 'invalid_state') {
    return kind === 'step3'
      ? copyText('requestSync.approvalLock.failed.reason.undecidedLegal')
      : copyText('requestSync.approvalLock.failed.reason.undecided', { status: approvalWord(step, ctx) })
  }

  // 403 step_completed: someone completed the step first (the refusal's rows are adopted,
  // so the step carries who).
  if (out.status === 'forbidden' && out.code === 'step_completed') {
    return copyText('requestSync.approvalLock.failed.reason.completed', { by: step.completed_by })
  }

  if (out.status === 'unknown') {
    return copyText('requestSync.approvalLock.failed.reason.unknown')
  }

  if (out.status === 'not-applied') {
    return copyText('requestSync.approvalLock.failed.reason.network')
  }

  // The remarks save that has to come first failed: its own section says why.
  if (out.status === 'content-not-saved') {
    return copyText('requestSync.approvalLock.failed.reason.remarks')
  }

  var message = out.err && out.err.response && out.err.response.data ? out.err.response.data.message : null

  return copyText('requestSync.approvalLock.failed.reason.other', { message: message })
}

/**
 * The ONE section a closing drawer raises when some of its locks did not go
 * through: the locked rows, then the not-locked ones with a reason, and the footer
 * that says the page stays open. `items` = [{ step, out }] in the order tried
 * (`out` is the runCommand outcome, `out.ok` for a lock that went through).
 */
export function closeLockNotice (items, ctx) {
  var failed = items.filter(function (i) { return !i.out.ok })
  var lines = []

  items.forEach(function (i) {
    if (i.out.ok) {
      lines.push(copyText('requestSync.approvalLock.failed.lockedRow', { n: i.step.stepNo, title: stepTitle(ctx.action, i.step) }))
    }
  })

  failed.forEach(function (i) {
    lines.push(copyText('requestSync.approvalLock.failed.notLockedRow', {
      n: i.step.stepNo,
      title: stepTitle(ctx.action, i.step),
      reason: failedReason(i, ctx)
    }))
  })

  var unknown = failed.some(function (i) { return i.out.status === 'unknown' })
  var footer = copyText('requestSync.approvalLock.failed.footer', { remaining: failed.length })

  if (unknown) {
    footer += ' ' + copyText('requestSync.approvalLock.failed.footerUnknown')
  }

  return {
    type: 'warning',
    title: copyText('requestSync.approvalLock.failed.title', { failed: failed.length, total: items.length }),
    lines: lines,
    footer: footer,
    duration: 0
  }
}

/**
 * A record-level refusal of a completed step (403 step_completed on a file action,
 * equipment, backhaul, Create Sharer, or the lock line of a stale screen): one C-12
 * section, built AFTER the request was re-read, so the party-1 row can name who
 * completed the step and when. `opts`: { n, what, kind, p1 } with `what` a key of
 * requestSync.completed.what.*, `kind` 'change' | 'lock' | 'unlock' and `p1` the
 * adopted party-1 row (or null: the lead then has no name). `replaceKey` makes a repeat
 * refusal replace its section instead of adding another.
 */
export function stepCompletedNotice (root, opts) {
  return {
    type: 'warning',
    title: copyText('requestSync.completed.title', { n: opts.n }),
    lead: completedLeadFor(root, { n: opts.n, p1: opts.p1 || null, what: opts.what }),
    footer: completedFooter(root, opts.n, opts.kind),
    replaceKey: 'step_completed:' + opts.n + ':' + opts.what,
    duration: 0
  }
}
